import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    formatCurrency,
    formatDate,
    formatPrintAmount,
    formatPrintAmountInWords,
    formatPrintDate,
    formatPrintQty,
    getCustomerPrintLines,
    getDeliveryNoteGroupedItems,
    getDeliveryNoteRefLine,
    getItemPrintDescription,
    getTaxInvoiceLineItems,
    computeAmountExclVat,
    getText,
    fieldIfValue,
    toPrintUpper,
} from './printHelpers';
import { buildPrintHtml } from './printPagination';

const TAX_INVOICE_DETAIL_FIELDS = [
    {
        label: 'INVOICE NUMBER:',
        getValue: (data) => getText(data.invoice_number) || getText(data.number),
    },
    {
        label: 'INVOICE DATE:',
        getValue: (data) => formatPrintDate(data.invoice_date || data.date),
    },
    {
        label: 'LPO NUMBER:',
        getValue: (data) => getText(data.lpo_number?.label) || getText(data.lpo_number),
    },
    {
        label: 'LPO DATE:',
        getValue: (data) => formatPrintDate(data.lpo_date),
    },
];

const DELIVERY_NOTE_DETAIL_FIELDS = [
    {
        label: 'DELIVERY NOTE NO:',
        getValue: (data) => getText(data.invoice_number) || getText(data.number),
    },
    {
        label: 'DATE:',
        getValue: (data) => formatPrintDate(data.invoice_date || data.delivery_date),
    },
    {
        label: 'LPO NUMBER:',
        getValue: (data) => getText(data.lpo_number?.label) || getText(data.lpo_number),
    },
    {
        label: 'LPO DATE:',
        getValue: (data) => formatPrintDate(data.lpo_date),
    },
];

const DOCUMENT_CONFIG = {
    cashMemo: {
        title: 'TAX INVOICE',
        detailsTitle: 'Cash Memo Details',
        cssFile: '/css/new_document_print.css',
        bodyClass: 'tax-invoice',
        isTaxInvoice: true,
        showDeliveryNotesList: false,
        showTotals: true,
        showAmountInWords: true,
        showReceivedBy: false,
        showAdvancePayment: true,
    },
    cashMemoDirect: {
        title: 'TAX INVOICE',
        detailsTitle: 'Cash Memo Direct Details',
        cssFile: '/css/new_document_print.css',
        bodyClass: 'tax-invoice',
        isTaxInvoice: true,
        showDeliveryNotesList: false,
        showTotals: true,
        showAmountInWords: true,
        showReceivedBy: false,
        showAdvancePayment: false,
    },
    deliveryNote: {
        title: 'DELIVERY NOTE',
        detailsTitle: 'Delivery Note Details',
        cssFile: '/css/new_document_print.css',
        bodyClass: 'delivery-note',
        isTaxInvoice: false,
        showDeliveryNotesList: false,
        showTotals: false,
        showAmountInWords: false,
        showReceivedBy: true,
        showAdvancePayment: false,
    },
    saleInvoice: {
        title: 'TAX INVOICE',
        detailsTitle: 'Sale Invoice Details',
        cssFile: '/css/new_document_print.css',
        bodyClass: 'tax-invoice',
        isTaxInvoice: true,
        showDeliveryNotesList: true,
        showTotals: true,
        showAmountInWords: true,
        showReceivedBy: false,
        showAdvancePayment: false,
    },
};

const getDocumentDetailFields = (data, isTaxInvoice) => {
    const defs = isTaxInvoice ? TAX_INVOICE_DETAIL_FIELDS : DELIVERY_NOTE_DETAIL_FIELDS;
    return defs
        .map((def) => fieldIfValue(def.label, def.getValue(data)))
        .filter(Boolean);
};

const PrintCustomerBlock = ({ data }) => {
    const lines = getCustomerPrintLines(data);
    if (lines.length === 0) return null;
    return (
        <section id="client-info">
            {lines.map((line, idx) => {
                if (line.kind === 'labeled') {
                    return (
                        <div key={idx} className="client-labeled">
                            <span className="client-label">{line.label}</span>
                            <span className="client-value">{line.value}</span>
                        </div>
                    );
                }
                return <span key={idx} className="client-line">{line.text}</span>;
            })}
        </section>
    );
};

const PrintDocumentDetails = ({ data, isTaxInvoice }) => {
    const fields = getDocumentDetailFields(data, isTaxInvoice);
    if (fields.length === 0) return null;
    return (
        <section id="invoice-info">
            {fields.map((field, idx) => (
                <div key={idx} className="doc-field">
                    <span className="doc-field-label">{field.label}</span>
                    <span className="doc-field-value">{field.value}</span>
                </div>
            ))}
        </section>
    );
};

const DeliveryNotesListBlock = ({ data }) => {
    const refLine = getDeliveryNoteRefLine(data);
    if (!refLine) return null;
    return (
        <section id="delivery-notes-list">
            <span className="delivery-note-ref-label">DEL. NOTE REF:</span>{' '}
            <span className="delivery-note-ref-values">{refLine}</span>
        </section>
    );
};

const TaxInvoiceTable = ({ data, documentType }) => {
    const lineItems = getTaxInvoiceLineItems(data, documentType);
    return (
        <table className="print-table" cellSpacing="0" cellPadding="0">
            <thead>
                <tr>
                    <th className="col-item">ITEM</th>
                    <th className="col-qty">QTY</th>
                    <th className="col-unit">UNIT</th>
                    <th className="col-price">PRICE</th>
                    <th className="col-amt">AMT. EXCL. VAT</th>
                </tr>
            </thead>
            <tbody>
                {lineItems.length > 0 ? lineItems.map((row, idx) => {
                    const desc = getItemPrintDescription(row);
                    const unit = toPrintUpper(getText(row.unit) || getText(row.item?.unit) || '');
                    const qty = formatPrintQty(row.quantity ?? row.qty ?? 0);
                    const price = formatPrintAmount(row.unit_price ?? row.price ?? 0);
                    const excl = formatPrintAmount(computeAmountExclVat(row));
                    return (
                        <tr key={idx}>
                            <td className="col-item">{desc}</td>
                            <td className="col-qty">{qty}</td>
                            <td className="col-unit">{unit}</td>
                            <td className="col-price">{price}</td>
                            <td className="col-amt">{excl}</td>
                        </tr>
                    );
                }) : (
                    <tr><td colSpan="5" className="col-item">&nbsp;</td></tr>
                )}
            </tbody>
        </table>
    );
};

const DeliveryNoteTable = ({ data }) => {
    const groupedItems = getDeliveryNoteGroupedItems(data);
    return (
        <table className="print-table" cellSpacing="0" cellPadding="0">
            <thead>
                <tr>
                    <th className="col-no">NO.</th>
                    <th className="col-name">NAME &amp; STAFF NO.</th>
                    <th className="col-desc">DESCRIPTION</th>
                    <th className="col-qty">QTY</th>
                    <th className="col-unit">UNIT</th>
                    <th className="col-job">JOB NO.</th>
                </tr>
            </thead>
            <tbody>
                {groupedItems.length > 0 ? groupedItems.flatMap((group, groupIndex) => (
                    (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => {
                        const serial = group.serial_number || groupIndex + 1;
                        const employeeName = rowIndex === 0 ? toPrintUpper(getText(group.employee_name)) : '';
                        const desc = getItemPrintDescription(row);
                        const qty = formatPrintQty(row.quantity || row.qty || 0);
                        const unit = toPrintUpper(getText(row.unit) || getText(row.item?.unit) || '');
                        const jobNo = toPrintUpper(getText(group.job_order_number) || getText(row.job_order_number) || '');
                        return (
                            <tr key={`${group.job_order_id || groupIndex}-${row.id || rowIndex}`}>
                                <td className="col-no">{rowIndex === 0 ? serial : ''}</td>
                                <td className="col-name">{employeeName}</td>
                                <td className="col-desc">{desc}</td>
                                <td className="col-qty">{qty}</td>
                                <td className="col-unit">{unit}</td>
                                <td className="col-job">{jobNo}</td>
                            </tr>
                        );
                    })
                )) : (
                    <tr><td colSpan="6" className="col-desc">&nbsp;</td></tr>
                )}
            </tbody>
        </table>
    );
};

const PrintTotalsFooter = ({ data, config }) => {
    if (!config.showTotals && !config.showAmountInWords) return null;

    if (!config.showTotals) {
        return (
            <section id="sums">
                <div className="invoice_amount_in_words">
                    {formatPrintAmountInWords(data.net_total)}
                </div>
            </section>
        );
    }

    const totalRows = [
        { label: 'GROSS AMOUNT:', value: formatPrintAmount(data.gross_total) },
        { label: 'DISCOUNT:', value: formatPrintAmount(data.discount_amount) },
        { label: 'SUB TOTAL:', value: formatPrintAmount(data.sub_total) },
        { label: 'VAT @ 10%:', value: formatPrintAmount(data.vat_amount) },
        { label: 'NET AMOUNT:', value: formatPrintAmount(data.net_total), net: true },
    ];

    return (
        <section id="sums">
            <table id="sums-table" cellSpacing="0" cellPadding="0">
                <tbody>
                    {totalRows.map((row, idx) => (
                        <tr key={row.label} className={row.net ? 'amount-total net' : 'amount-total'}>
                            {idx === 0 && (
                                <td rowSpan={totalRows.length} className="invoice_amount_in_words">
                                    {config.showAmountInWords ? formatPrintAmountInWords(data.net_total) : '\u00a0'}
                                </td>
                            )}
                            <td className="total-label">{row.label}</td>
                            <td className="total-value">{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};

const PrintSourceDocument = ({ config, documentType, data }) => (
    <div id="container" className={config.bodyClass}>
        <div className="print-page-header">
            <div className="title">{config.title}</div>
            <section className="middle-box">
                <PrintCustomerBlock data={data} />
                <PrintDocumentDetails data={data} isTaxInvoice={config.isTaxInvoice} />
            </section>
            {config.showDeliveryNotesList && <DeliveryNotesListBlock data={data} />}
        </div>
        <section id="items">
            {config.isTaxInvoice
                ? <TaxInvoiceTable data={data} documentType={documentType} />
                : <DeliveryNoteTable data={data} />}
        </section>
        <div className="print-last-page-footer" aria-hidden="true">
            <PrintTotalsFooter data={data} config={config} />
        </div>
    </div>
);

const PrintModal = ({ config, documentType, data, onClose, printRef }) => {
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow || !printRef.current) return;

        const baseUrl = window.location.origin + (process.env.PUBLIC_URL || '');
        const cssHref = `${baseUrl}${config.cssFile}`;
        const sourceEl = printRef.current.querySelector('#print-source');

        const doPrint = () => {
            const paginatedHtml = sourceEl
                ? buildPrintHtml(sourceEl, { showReceivedBy: config.showReceivedBy })
                : printRef.current.innerHTML;
            const target = printWindow.document.getElementById('print-content');
            if (target) target.innerHTML = paginatedHtml;
            printWindow.document.close();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 150);
        };

        printWindow.document.write(`
            <html>
                <head>
                    <title>${config.title} Print</title>
                    <link rel="stylesheet" href="${cssHref}" id="print-css-link" />
                </head>
                <body>
                    <div id="print-content"></div>
                </body>
            </html>
        `);

        const linkEl = printWindow.document.getElementById('print-css-link');
        if (linkEl) {
            const fallback = setTimeout(doPrint, 3000);
            const done = () => { clearTimeout(fallback); doPrint(); };
            linkEl.onload = done;
            linkEl.onerror = done;
            if (linkEl.sheet) done();
        } else {
            doPrint();
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}>
            <div style={{
                backgroundColor: 'white', width: '90%', height: '90%',
                borderRadius: '8px', position: 'relative', overflow: 'hidden',
            }}>
                <div style={{
                    padding: '15px', borderBottom: '1px solid #ddd',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <h5 style={{ margin: 0 }}>Print Preview</h5>
                    <div>
                        <button className="btn btn-primary btn-sm me-2" onClick={handlePrint}>
                            <i className="fas fa-print me-1"></i>
                            Print
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={onClose}>
                            <i className="fas fa-times me-1"></i>
                            Close
                        </button>
                    </div>
                </div>
                <div
                    ref={printRef}
                    style={{ height: 'calc(100% - 70px)', overflow: 'auto', padding: '20px' }}
                    className="modal-print-content-document-print"
                >
                    <link rel="stylesheet" href={`${process.env.PUBLIC_URL || ''}/css/new_document_print.css`} />
                    <div id="print-source">
                        <PrintSourceDocument config={config} documentType={documentType} data={data} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const DocumentPrint = ({ data, isLoading, documentType = 'cashMemo' }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);
    const printRef = useRef(null);

    const config = DOCUMENT_CONFIG[documentType];

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (!config) {
        return (
            <div className="text-center p-4">
                <h5>Unknown document type: {documentType}</h5>
            </div>
        );
    }

    const fmt = (amount) => formatCurrency(amount);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const lineItems = config.isTaxInvoice
        ? getTaxInvoiceLineItems(data, documentType)
        : getDeliveryNoteGroupedItems(data);

    const detailFields = getDocumentDetailFields(data, config.isTaxInvoice);

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">{config.detailsTitle}</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary btn-sm no-print"
                        onClick={() => setShowPrintModal(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            <div className="flex-grow-1">
                <div className="table-responsive mt-2">
                    <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                        <tbody>
                            {detailFields.reduce((rows, field, idx) => {
                                if (idx % 2 === 0) {
                                    const next = detailFields[idx + 1];
                                    rows.push(
                                        <tr key={field.label}>
                                            <td className="fw-bold">{field.label.replace(/:$/, '')}</td>
                                            <td>{field.value}</td>
                                            {next ? (
                                                <>
                                                    <td className="fw-bold">{next.label.replace(/:$/, '')}</td>
                                                    <td>{next.value}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td></td>
                                                    <td></td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                }
                                return rows;
                            }, [])}
                            <tr>
                                <td className="fw-bold">Customer</td>
                                <td>{getText(data.customer_name) || getText(data.customer?.label) || '-'}</td>
                                <td className="fw-bold">Phone</td>
                                <td>{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {config.showDeliveryNotesList && getDeliveryNoteRefLine(data) && (
                    <div className="mt-2">
                        <strong>DEL. NOTE REF:</strong>{' '}
                        {getDeliveryNoteRefLine(data)}
                    </div>
                )}

                <div className="table-responsive mt-4">
                    {config.isTaxInvoice ? (
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3">Item Name</th>
                                    <th className="text-nowrap p-3">Qty</th>
                                    <th className="text-nowrap p-3">Unit</th>
                                    <th className="text-nowrap p-3">Unit Price</th>
                                    <th className="text-nowrap p-3">Amt Excl VAT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.length > 0 ? lineItems.map((row, index) => (
                                    <tr key={index}>
                                        <td className="p-3 align-middle">{getItemPrintDescription(row) || getText(row.item_description) || '-'}</td>
                                        <td className="p-3 align-middle">{formatPrintQty(row.quantity ?? row.qty ?? 0)}</td>
                                        <td className="p-3 align-middle">{getText(row.unit) || '-'}</td>
                                        <td className="p-3 align-middle">{fmt(row.unit_price ?? row.price ?? 0)}</td>
                                        <td className="p-3 align-middle">{fmt(computeAmountExclVat(row))}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="text-center">No items added</td></tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3">No.</th>
                                    <th className="text-nowrap p-3">Name &amp; Staff No.</th>
                                    <th className="text-nowrap p-3">Description</th>
                                    <th className="text-nowrap p-3">Qty</th>
                                    <th className="text-nowrap p-3">Unit</th>
                                    <th className="text-nowrap p-3">Job No.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.length > 0 ? lineItems.flatMap((group, groupIndex) => (
                                    (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => (
                                        <tr key={`${group.job_order_id || groupIndex}-${row.id || rowIndex}`}>
                                            <td className="text-nowrap p-3 text-center">
                                                {rowIndex === 0 ? (group.serial_number || groupIndex + 1) : ''}
                                            </td>
                                            <td className="p-3 text-nowrap">
                                                {rowIndex === 0 ? getText(group.employee_name) : ''}
                                            </td>
                                            <td className="p-3">{getText(row.item_description) || '-'}</td>
                                            <td className="text-end p-3">{formatPrintQty(row.quantity || row.qty || 0)}</td>
                                            <td className="text-center p-3">{getText(row.unit) || '-'}</td>
                                            <td className="text-nowrap p-3">
                                                {getText(group.job_order_number) || getText(row.job_order_number) || '-'}
                                            </td>
                                        </tr>
                                    ))
                                )) : (
                                    <tr><td colSpan="6" className="text-center p-3">No items available</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {config.showTotals && (
                    <div className="row mt-4 d-flex justify-content-end">
                        <div className="col-md-4">
                            <div className="mb-2 row">
                                <label className="col-6 col-form-label fw-bold">Net Total:</label>
                                <div className="col-6">
                                    <div className="form-control-plaintext text-end fw-bold">{fmt(data.net_total)}</div>
                                </div>
                            </div>
                            {config.showAdvancePayment && (
                                <>
                                    <div className="mb-2 row">
                                        <label className="col-6 col-form-label fw-bold">Advance:</label>
                                        <div className="col-6">
                                            <div className="form-control-plaintext text-end">{fmt(data.advance_payment)}</div>
                                        </div>
                                    </div>
                                    <div className="mb-2 row">
                                        <label className="col-6 col-form-label fw-bold">Payment:</label>
                                        <div className="col-6">
                                            <div className="form-control-plaintext text-end">
                                                {fmt((data.balance ?? 0) - (data.advance_payment ?? 0))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showPrintModal && (
                <PrintModal
                    config={config}
                    documentType={documentType}
                    data={data}
                    onClose={() => setShowPrintModal(false)}
                    printRef={printRef}
                />
            )}
        </div>
    );
};

export default DocumentPrint;
